import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Portal,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import SchoolIcon from '@mui/icons-material/School';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

/**
 * Shared step-by-step tutorial, extracted from the Purchase Planner tutorial so
 * every Super Calculator page behaves the same: a vertical stepper, a
 * "Show on Page" button that highlights the real control, progress persisted in
 * localStorage, and a downloadable text manual.
 */

export interface TutorialStepDef {
  title: string;
  content: string;
  /**
   * CSS selector of the control this step describes — e.g.
   * `[data-tutorial="purchase-price"]`. When set, the step offers
   * "Show on Page", which scrolls to and pulses that element.
   */
  targetElement?: string | null;
}

interface TutorialProgress {
  lastStep: number;
  completed: boolean;
  started: boolean;
}

const EMPTY_PROGRESS: TutorialProgress = { lastStep: 0, completed: false, started: false };

const readProgress = (storageKey: string): TutorialProgress => {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved) return { ...EMPTY_PROGRESS, ...JSON.parse(saved) };
  } catch (error) {
    console.error('Error loading tutorial progress:', error);
  }
  return EMPTY_PROGRESS;
};

const writeProgress = (storageKey: string, progress: TutorialProgress) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(progress));
  } catch (error) {
    console.error('Error saving tutorial progress:', error);
  }
};

export interface CalculatorTutorialController {
  open: boolean;
  step: number;
  progress: TutorialProgress;
  /** Red = not started, amber = in progress, green = completed. */
  buttonColor: 'success' | 'warning' | 'error';
  stepCount: number;
  openTutorial: () => void;
  closeTutorial: () => void;
  goToStep: (step: number) => void;
  restart: () => void;
  finish: () => void;
}

/** Owns tutorial open/step state and the localStorage progress for one calculator. */
export function useCalculatorTutorial(
  storageKey: string,
  stepCount: number,
): CalculatorTutorialController {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState<TutorialProgress>(() => readProgress(storageKey));

  const persist = useCallback(
    (next: TutorialProgress) => {
      setProgress(next);
      writeProgress(storageKey, next);
    },
    [storageKey],
  );

  const goToStep = useCallback(
    (nextStep: number) => {
      const clamped = Math.min(Math.max(0, nextStep), Math.max(0, stepCount - 1));
      setStep(clamped);
      persist({ lastStep: clamped, completed: false, started: true });
    },
    [persist, stepCount],
  );

  const openTutorial = useCallback(() => {
    const resume = Math.min(progress.lastStep || 0, Math.max(0, stepCount - 1));
    setStep(resume);
    setOpen(true);
    persist({ lastStep: resume, completed: progress.completed, started: true });
  }, [persist, progress.completed, progress.lastStep, stepCount]);

  const closeTutorial = useCallback(() => setOpen(false), []);

  const finish = useCallback(() => {
    persist({ lastStep: stepCount, completed: true, started: true });
    setOpen(false);
    setStep(0);
  }, [persist, stepCount]);

  const restart = useCallback(() => {
    setStep(0);
    persist(EMPTY_PROGRESS);
  }, [persist]);

  const buttonColor: 'success' | 'warning' | 'error' = progress.completed
    ? 'success'
    : progress.started
      ? 'warning'
      : 'error';

  return {
    open,
    step,
    progress,
    buttonColor,
    stepCount,
    openTutorial,
    closeTutorial,
    goToStep,
    restart,
    finish,
  };
}

interface TutorialLaunchButtonProps {
  controller: CalculatorTutorialController;
  /** Calculator name used in the tooltip, e.g. "Maximizer". */
  label: string;
  size?: 'small' | 'medium';
}

/** School-cap button that opens the tutorial; its colour tracks progress. */
export const TutorialLaunchButton: React.FC<TutorialLaunchButtonProps> = ({
  controller,
  label,
  size = 'small',
}) => {
  const status = controller.progress.completed
    ? ' (Completed)'
    : controller.progress.started
      ? ' (In Progress)'
      : ' (Not Started)';

  return (
    <Tooltip title={`Tutorial - Learn how to use ${label}${status}`}>
      <IconButton
        onClick={controller.openTutorial}
        color={controller.buttonColor}
        size={size}
        sx={{
          border: '2px solid',
          borderColor: 'currentColor',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <SchoolIcon fontSize={size === 'small' ? 'small' : 'medium'} />
      </IconButton>
    </Tooltip>
  );
};

/** Builds the plain-text manual offered by the download button in the dialog header. */
export const buildTutorialManual = (
  title: string,
  steps: TutorialStepDef[],
  extraSections: { heading: string; body: string }[] = [],
): string => {
  const rule = '='.repeat(80);
  const toc = steps.map((s, i) => `${i + 1}. ${s.title}`).join('\n');
  const extraToc = extraSections
    .map((s, i) => `${steps.length + i + 1}. ${s.heading}`)
    .join('\n');
  const body = steps
    .map(
      (s, i) => `${rule}\n${i + 1}. ${s.title.toUpperCase()}\n${rule}\n\n${s.content}\n`,
    )
    .join('\n');
  const extras = extraSections
    .map(
      (s, i) =>
        `${rule}\n${steps.length + i + 1}. ${s.heading.toUpperCase()}\n${rule}\n\n${s.body}\n`,
    )
    .join('\n');

  return `${title.toUpperCase()} - USER MANUAL
Generated: ${new Date().toLocaleString('en-SG')}

${rule}
TABLE OF CONTENTS
${rule}

${toc}${extraToc ? `\n${extraToc}` : ''}

${body}${extras ? `\n${extras}` : ''}
${rule}
Disclaimer: figures produced by this calculator are estimates for planning
purposes only and are subject to verification and approval by the relevant
financial institutions and authorities.
${rule}
`;
};

interface CalculatorTutorialProps {
  controller: CalculatorTutorialController;
  /** Dialog heading, e.g. "Maximizer Tutorial". */
  title: string;
  steps: TutorialStepDef[];
  /** Calculator name used for the downloaded manual file, e.g. "Maximizer". */
  manualName: string;
  /** Reference sections appended after the step-by-step part of the manual. */
  manualExtraSections?: { heading: string; body: string }[];
}

/** The tutorial dialog itself, plus the on-page highlight it drives. */
export const CalculatorTutorial: React.FC<CalculatorTutorialProps> = ({
  controller,
  title,
  steps,
  manualName,
  manualExtraSections,
}) => {
  const { open, step, stepCount } = controller;
  const [pointerTarget, setPointerTarget] = useState<string | null>(null);
  const [pointerVisible, setPointerVisible] = useState(false);
  const [dialogMinimized, setDialogMinimized] = useState(false);
  const [pointerMissing, setPointerMissing] = useState(false);
  const timers = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  }, []);

  const resetPointer = useCallback(() => {
    clearTimers();
    setPointerVisible(false);
    setPointerTarget(null);
    setDialogMinimized(false);
    setPointerMissing(false);
  }, [clearTimers]);

  // Drop the highlight whenever the step changes or the dialog closes.
  useEffect(() => {
    resetPointer();
  }, [step, open, resetPointer]);

  useEffect(() => clearTimers, [clearTimers]);

  /** Fade the dialog back, pulse the target for 3s, then restore the dialog. */
  const showOnPage = (selector: string) => {
    clearTimers();
    if (!document.querySelector(selector)) {
      setPointerMissing(true);
      return;
    }
    setPointerMissing(false);
    setDialogMinimized(true);
    setPointerTarget(selector);

    timers.current.push(
      window.setTimeout(() => {
        setPointerVisible(true);
        timers.current.push(
          window.setTimeout(() => {
            setPointerVisible(false);
            timers.current.push(
              window.setTimeout(() => {
                setDialogMinimized(false);
                timers.current.push(
                  window.setTimeout(() => setPointerTarget(null), 300),
                );
              }, 300),
            );
          }, 3000),
        );
      }, 300),
    );
  };

  const handleClose = () => {
    resetPointer();
    controller.closeTutorial();
  };

  const downloadManual = () => {
    const content = buildTutorialManual(manualName, steps, manualExtraSections);
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${manualName.replace(/\s+/g, '_')}_Tutorial_Manual_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const activeStep = Math.min(step, Math.max(0, steps.length - 1));

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            transition: 'all 0.3s ease-in-out',
            opacity: dialogMinimized ? 0.3 : 1,
            transform: dialogMinimized ? 'scale(0.85) translateY(-20px)' : 'scale(1) translateY(0)',
            pointerEvents: dialogMinimized ? 'none' : 'auto',
          },
        }}
        BackdropProps={{
          sx: {
            transition: 'all 0.3s ease-in-out',
            backgroundColor: dialogMinimized ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" component="div" fontWeight={700}>
              {title}
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
              <Tooltip title="Download User Manual">
                <IconButton onClick={downloadManual} color="primary" size="small" sx={{ mr: 1 }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton onClick={handleClose} size="small">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {pointerMissing && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setPointerMissing(false)}>
              This control isn&apos;t on screen right now. Switch to the tab, mode or option the
              step describes, then tap <strong>Show on Page</strong> again.
            </Alert>
          )}
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((s, index) => {
              const done = index < activeStep;
              return (
                <Step
                  key={s.title}
                  completed={done}
                  sx={{ '& .MuiStepLabel-root .Mui-completed': { color: 'success.main' } }}
                >
                  <StepLabel
                    StepIconComponent={() => (
                      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {done ? (
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                        ) : (
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              border: '2px solid',
                              borderColor: index === activeStep ? 'primary.main' : 'grey.400',
                              backgroundColor: index === activeStep ? 'primary.main' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: index === activeStep ? 'white' : 'text.secondary',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {index + 1}
                          </Box>
                        )}
                      </Box>
                    )}
                  >
                    <Typography
                      variant="h6"
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                      onClick={() => controller.goToStep(index)}
                    >
                      {s.title}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2, lineHeight: 1.8 }}>
                      {s.content}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      {s.targetElement && (
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => showOnPage(s.targetElement as string)}
                          size="small"
                          startIcon={<ZoomInIcon />}
                          sx={{ borderStyle: 'dashed', fontWeight: 600, flexShrink: 0 }}
                        >
                          Show on Page
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (index < steps.length - 1) controller.goToStep(index + 1);
                          else controller.finish();
                        }}
                        size="small"
                        sx={{ flexShrink: 0 }}
                      >
                        {index < steps.length - 1 ? 'Next' : 'Finish'}
                      </Button>
                      {index > 0 && (
                        <Button
                          variant="outlined"
                          onClick={() => controller.goToStep(index - 1)}
                          size="small"
                          sx={{ flexShrink: 0 }}
                        >
                          Previous
                        </Button>
                      )}
                      <Button variant="text" onClick={handleClose} size="small" sx={{ flexShrink: 0 }}>
                        Skip Tutorial
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              Step {activeStep + 1} of {stepCount}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                onClick={controller.restart}
                startIcon={<ReplayIcon />}
                size="small"
                color="secondary"
                sx={{ flexShrink: 0 }}
              >
                Restart
              </Button>
              <Button
                onClick={() => controller.goToStep(activeStep - 1)}
                disabled={activeStep === 0}
                size="small"
                sx={{ flexShrink: 0 }}
              >
                Previous
              </Button>
              <Button
                onClick={() => {
                  if (activeStep < steps.length - 1) controller.goToStep(activeStep + 1);
                  else controller.finish();
                }}
                variant="contained"
                size="small"
                sx={{ flexShrink: 0 }}
              >
                {activeStep < steps.length - 1 ? 'Next' : 'Finish'}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {pointerTarget && pointerVisible && (
        <Portal>
          <TutorialPointer selector={pointerTarget} />
        </Portal>
      )}
    </>
  );
};

/** Pulsing outline drawn over the element a step points at. */
const TutorialPointer: React.FC<{ selector: string }> = ({ selector }) => {
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

  useEffect(() => {
    const updatePosition = () => {
      const element = document.querySelector(selector);
      if (!element) {
        setCoords(null);
        return;
      }
      element.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      const rect = element.getBoundingClientRect();
      setCoords({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    };

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [selector]);

  if (!coords) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        width: `${coords.width}px`,
        height: `${coords.height}px`,
        border: '3px solid #6366F1',
        borderRadius: '8px',
        backgroundColor: 'rgba(99, 102, 241, 0.15)',
        pointerEvents: 'none',
        zIndex: 9998,
        animation: 'tutorialPulse 2s ease-in-out infinite, tutorialFadeIn 0.3s ease-in',
        '@keyframes tutorialPulse': {
          '0%, 100%': { boxShadow: '0 0 0 0 rgba(99, 102, 241, 0.7)' },
          '50%': { boxShadow: '0 0 0 15px rgba(99, 102, 241, 0)' },
        },
        '@keyframes tutorialFadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}
    />
  );
};

export default CalculatorTutorial;
